import { Button } from "@/components/ui/button";
import type { paymentStatus } from "@/types/topup/summary";
import { Clock, Loader2 } from "lucide-react";

type ConfirmationProcessingProps = {
  status?: paymentStatus;
  timedOut?: boolean;
  checking?: boolean;
  onCheckStatus?: () => void;
};

const TEXT: Record<string, { title: string; hint: string }> = {
  pending_payment: {
    title: "Waiting for payment",
    hint: "We're confirming your payment. This can take a moment.",
  },
  default: {
    title: "Loading your booking",
    hint: "Please wait a moment.",
  },
};

export function ConfirmationProcessing({
  status,
  timedOut,
  checking = false,
  onCheckStatus,
}: ConfirmationProcessingProps) {
  const t = timedOut
    ? {
        title: "Still processing",
        hint: "This is taking longer than expected. You can check your booking status later.",
      }
    : (TEXT[status ?? "default"] ?? TEXT.default);

  return (
    <section className="fixed inset-0 z-50 flex items-center justify-center bg-primary-foreground px-4">
      <div className="w-full max-w-xl flex flex-col items-center bg-card shadow-xl rounded-2xl py-20 text-center animate-fade-up">
        <div className="grid size-14 place-items-center rounded-full border border-muted bg-primary-foreground">
          {timedOut ? (
            <Clock className="size-6 text-secondary-foreground" />
          ) : (
            <Loader2 className="size-6 animate-spin text-secondary-foreground" />
          )}
        </div>
        <h1 className="mt-5 text-xs font-bold uppercase tracking-[0.22em] text-secondary-foreground">
          {t.title}
        </h1>
        <p className="mt-2 max-w-sm text-sm text-secondary-foreground">{t.hint}</p>
        {!timedOut && (
          <p className="mt-6 text-[11px] text-secondary-foreground/40">
            Please don't close or refresh this page.
          </p>
        )}

        {timedOut && (
          <Button
            variant="outline"
            className="cursor-pointer h-12 mt-4"
            onClick={onCheckStatus}
            disabled={checking}
          >
            {checking ? (
              <>
                <Loader2 className="animate-spin" />
                Checking...
              </>
            ) : (
              "Check status again"
            )}
          </Button>
        )}
      </div>
    </section>
  );
}
